import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cartService, CartItem } from '../services/cartService';

const CartPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    if (user) setCartItems(cartService.getCartItems(user.id));
  }, [user]);

  const handleUpdateQuantity = (id: string, qty: number) => {
    if (!user || qty < 1) return;
    setCartItems(cartService.updateQuantity(user.id, id, qty));
  };

  const handleRemove = (id: string) => {
    if (!user) return;
    setCartItems(cartService.removeItem(user.id, id));
  };

  const handleClear = () => {
    if (!user || !window.confirm('Clear your entire cart?')) return;
    cartService.clearCart(user.id);
    setCartItems([]);
  };

  const total = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-2">Please login to view your cart</h2>
          <button onClick={() => navigate('/login')} className="bg-[#DAA520] text-white px-6 py-2 rounded hover:bg-[#c5941c] mt-4">
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Your Shopping Cart</h1>

          {cartItems.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">🛒</div>
              <h3 className="text-2xl font-semibold mb-3">Your cart is empty</h3>
              <p className="text-gray-600 mb-6">Add some products to get started.</p>
              <button onClick={() => navigate('/')} className="bg-[#DAA520] text-white px-8 py-3 rounded font-semibold hover:bg-[#c5941c]">
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-xl font-semibold">Cart Items ({cartItems.length})</h2>
                    <button onClick={handleClear} className="text-red-600 hover:text-red-800 text-sm font-medium">Clear Cart</button>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {cartItems.map(item => (
                      <div key={item.id} className="p-6 flex gap-6">
                        <div className="w-28 h-28 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                          <img src={encodeURI(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">{item.name}</h3>
                              <p className="text-gray-500 text-sm capitalize">{item.category}</p>
                              {item.selectedSize && <p className="text-gray-500 text-sm">Size: {item.selectedSize}</p>}
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-[#DAA520]">₹{(item.price * item.quantity).toFixed(0)}</p>
                              <p className="text-gray-400 text-sm">₹{item.price.toFixed(0)} each</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-4">
                            <div className="border border-gray-300 rounded flex items-center">
                              <button onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)} className="px-3 py-1 text-gray-600 hover:text-gray-800" disabled={item.quantity <= 1}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                              </button>
                              <span className="px-4 py-1 font-semibold">{item.quantity}</span>
                              <button onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)} className="px-3 py-1 text-gray-600 hover:text-gray-800">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                              </button>
                            </div>
                            <button onClick={() => handleRemove(item.id)} className="text-red-600 hover:text-red-800 text-sm font-medium">Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
                  <h2 className="text-xl font-semibold mb-6">Order Summary</h2>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>₹{total.toFixed(0)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Shipping</span><span>₹0</span></div>
                    <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-bold">
                      <span>Total</span><span className="text-[#DAA520]">₹{total.toFixed(0)}</span>
                    </div>
                  </div>
                  <button onClick={() => navigate('/checkout')} className="w-full bg-[#E91E63] text-white py-3 rounded font-semibold hover:bg-[#D81B60] mb-3">
                    Proceed to Checkout
                  </button>
                  <button onClick={() => navigate('/')} className="w-full border border-[#DAA520] text-[#DAA520] py-3 rounded font-semibold hover:bg-[#DAA520]/10">
                    Continue Shopping
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPage;
